import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import {
  FiActivity,
  FiDollarSign,
  FiInfo,
  FiRefreshCw,
  FiTrendingUp,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { PriceSuggestionService } from '../../services';

const CONDITIONS = ['Excellent', 'Good', 'Average', 'Poor'];

const formatINR = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const confidenceStyle = (level) => {
  switch (level) {
    case 'high':
      return 'bg-green-100 text-green-700';
    case 'medium':
      return 'bg-yellow-100 text-yellow-700';
    default:
      return 'bg-red-100 text-red-700';
  }
};

const PriceSuggestion = () => {
  const [options, setOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const currentYear = new Date().getFullYear();

  const [form, setForm] = useState({
    model: '',
    year: '',
    condition: '',
    km: '',
    ownerNo: '',
    transmission: '',
    fuelType: '',
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setOptionsLoading(true);
      try {
        const res = await PriceSuggestionService.getOptions();
        if (!cancelled) setOptions(res?.data || null);
      } catch (error) {
        // interceptor already surfaces a toast
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const modelList = useMemo(() => {
    if (!options?.models) return [];
    return options.models.map((m) => ({
      value: m.model,
      label: `${m.brand} ${m.model}`,
      brand: m.brand,
    }));
  }, [options]);

  const yearOptions = useMemo(() => {
    const min = options?.yearRange?.min ?? currentYear - 15;
    const max = options?.yearRange?.max ?? currentYear;
    const years = [];
    for (let y = max; y >= min; y -= 1) years.push(y);
    return years;
  }, [options, currentYear]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleReset = () => {
    setForm({
      model: '',
      year: '',
      condition: '',
      km: '',
      ownerNo: '',
      transmission: '',
      fuelType: '',
    });
    setResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.model || !form.year || !form.condition) {
      toast.error('Please select model, year and condition.');
      return;
    }

    const selected = modelList.find((m) => m.value === form.model);

    const payload = {
      brand: selected?.brand,
      model: form.model,
      year: Number(form.year),
      condition: form.condition,
    };
    if (form.km !== '') payload.km = Number(form.km);
    if (form.ownerNo !== '') payload.ownerNo = Number(form.ownerNo);
    if (form.transmission) payload.transmission = form.transmission;
    if (form.fuelType) payload.fuelType = form.fuelType;

    setSubmitting(true);
    setResult(null);
    try {
      const res = await PriceSuggestionService.estimate(payload);
      setResult(res?.data || null);
    } catch (error) {
      // interceptor already surfaces a toast
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 focus:outline-none transition';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl gradient-bg">
            <FiTrendingUp className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold gradient-text">Price Suggestion</h1>
            <p className="text-sm text-gray-500">
              Get an approximate resale price from model, year & condition.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 card-shadow p-6"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Model */}
            <div>
              <label className={labelClass}>
                Model <span className="text-red-500">*</span>
              </label>
              <select
                className={inputClass}
                value={form.model}
                onChange={handleChange('model')}
                disabled={optionsLoading}
              >
                <option value="">
                  {optionsLoading ? 'Loading models…' : 'Select a model'}
                </option>
                {modelList.map((m) => (
                  <option key={m.label} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Year + Condition */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>
                  Year <span className="text-red-500">*</span>
                </label>
                <select
                  className={inputClass}
                  value={form.year}
                  onChange={handleChange('year')}
                >
                  <option value="">Year</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  Condition <span className="text-red-500">*</span>
                </label>
                <select
                  className={inputClass}
                  value={form.condition}
                  onChange={handleChange('condition')}
                >
                  <option value="">Condition</option>
                  {(options?.conditions || CONDITIONS).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="pt-1 pb-1">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                Optional — improves accuracy
              </p>
            </div>

            {/* KM + Owner */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>KM Driven</label>
                <input
                  type="number"
                  min="0"
                  placeholder="e.g. 45000"
                  className={inputClass}
                  value={form.km}
                  onChange={handleChange('km')}
                />
              </div>
              <div>
                <label className={labelClass}>Owner</label>
                <select
                  className={inputClass}
                  value={form.ownerNo}
                  onChange={handleChange('ownerNo')}
                >
                  <option value="">Any</option>
                  <option value="1">First Owner</option>
                  <option value="2">Second Owner</option>
                  <option value="3">Third Owner</option>
                  <option value="4">Fourth Owner &amp; Above</option>
                </select>
              </div>
            </div>

            {/* Transmission + Fuel */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Transmission</label>
                <select
                  className={inputClass}
                  value={form.transmission}
                  onChange={handleChange('transmission')}
                >
                  <option value="">Any</option>
                  {(options?.transmissions || ['Manual', 'Automatic']).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Fuel Type</label>
                <select
                  className={inputClass}
                  value={form.fuelType}
                  onChange={handleChange('fuelType')}
                >
                  <option value="">Any</option>
                  {(options?.fuelTypes || ['Petrol', 'Diesel', 'CNG', 'LPG', 'EV']).map(
                    (f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting || optionsLoading}
                className="flex-1 flex items-center justify-center gap-2 gradient-bg text-white font-semibold py-3 rounded-xl shadow-md hover:opacity-95 disabled:opacity-60 transition"
              >
                {submitting ? (
                  <>
                    <FiRefreshCw className="h-4 w-4 animate-spin" />
                    Estimating…
                  </>
                ) : (
                  <>
                    <FiActivity className="h-4 w-4" />
                    Get Price
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-3 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
              >
                Reset
              </button>
            </div>
          </form>
        </motion.div>

        {/* Result */}
        <div className="lg:col-span-3">
          {!result ? (
            <div className="h-full min-h-[320px] flex flex-col items-center justify-center bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-50 mb-4">
                <FiDollarSign className="h-8 w-8 text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700">
                Your price estimate will appear here
              </h3>
              <p className="text-sm text-gray-400 mt-1 max-w-sm">
                Choose a model, year and condition, then click “Get Price”. Add KM,
                owner and transmission for a sharper estimate.
              </p>
            </div>
          ) : (
            <motion.div
              key={result.suggestedPrice}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              {/* Headline price */}
              <div className="bg-white rounded-2xl border border-gray-100 card-shadow overflow-hidden">
                <div className="gradient-bg px-6 py-5 text-white">
                  <p className="text-sm/none opacity-90">Suggested Price</p>
                  <div className="mt-2 flex items-end gap-3 flex-wrap">
                    <span className="text-4xl font-bold tracking-tight">
                      {formatINR(result.suggestedPrice)}
                    </span>
                    <span
                      className={`mb-1 px-2.5 py-1 rounded-full text-xs font-semibold ${confidenceStyle(
                        result.confidence
                      )}`}
                    >
                      {String(result.confidence || '').toUpperCase()} CONFIDENCE
                    </span>
                  </div>
                  <p className="mt-2 text-sm opacity-90">
                    Expected range: {formatINR(result.priceRange?.min)} –{' '}
                    {formatINR(result.priceRange?.max)}
                  </p>
                </div>

                <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">Model</p>
                    <p className="font-medium text-gray-800">
                      {result.input?.brand} {result.input?.model}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Year</p>
                    <p className="font-medium text-gray-800">{result.input?.year}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Condition</p>
                    <p className="font-medium text-gray-800">
                      {result.input?.condition}
                    </p>
                  </div>
                  {result.input?.km != null && (
                    <div>
                      <p className="text-gray-400">KM Driven</p>
                      <p className="font-medium text-gray-800">
                        {Number(result.input.km).toLocaleString('en-IN')} km
                      </p>
                    </div>
                  )}
                  {result.input?.ownerNo != null && (
                    <div>
                      <p className="text-gray-400">Owner</p>
                      <p className="font-medium text-gray-800">
                        {result.input.ownerNo}
                      </p>
                    </div>
                  )}
                  {result.input?.transmission && (
                    <div>
                      <p className="text-gray-400">Transmission</p>
                      <p className="font-medium text-gray-800">
                        {result.input.transmission}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Breakdown */}
              <div className="bg-white rounded-2xl border border-gray-100 card-shadow p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  How this was calculated
                </h3>
                <div className="space-y-2 text-sm">
                  <Row
                    label="Base (reference sold price)"
                    value={formatINR(result.breakdown?.basePrice)}
                  />
                  <Row
                    label="Year adjustment"
                    value={`× ${result.breakdown?.yearFactor}`}
                  />
                  <Row
                    label="Condition adjustment"
                    value={`× ${result.breakdown?.conditionFactor}`}
                  />
                  {result.breakdown?.kmFactor !== 1 && (
                    <Row
                      label="KM adjustment"
                      value={`× ${result.breakdown?.kmFactor}`}
                    />
                  )}
                  {result.breakdown?.ownerFactor !== 1 && (
                    <Row
                      label="Owner adjustment"
                      value={`× ${result.breakdown?.ownerFactor}`}
                    />
                  )}
                  {result.breakdown?.transmissionFactor !== 1 && (
                    <Row
                      label="Transmission adjustment"
                      value={`× ${result.breakdown?.transmissionFactor}`}
                    />
                  )}
                  <div className="border-t border-gray-100 my-2" />
                  <Row
                    label="Estimated value"
                    value={formatINR(result.breakdown?.rawPrice)}
                    strong
                  />
                </div>

                {result.reference && (
                  <div className="mt-4 flex items-start gap-2 rounded-xl bg-orange-50 p-3 text-xs text-orange-800">
                    <FiInfo className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Based on reference: {result.reference.brand}{' '}
                      {result.reference.model} {result.reference.variant} (
                      {result.reference.year}, {result.reference.condition},{' '}
                      {Number(result.reference.kmDriven).toLocaleString('en-IN')} km) sold
                      at {formatINR(result.reference.soldPrice)}.
                      {result.confidenceReasons?.length
                        ? ` ${result.confidenceReasons.join(' ')}`
                        : ''}
                    </span>
                  </div>
                )}

                <p className="mt-3 text-[11px] text-gray-400">
                  Rule-based estimate for guidance only — actual sale price varies with
                  demand, paperwork and negotiation.
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, strong }) => (
  <div className="flex items-center justify-between">
    <span className={strong ? 'text-gray-800 font-semibold' : 'text-gray-500'}>
      {label}
    </span>
    <span className={strong ? 'text-gray-900 font-bold' : 'text-gray-700 font-medium'}>
      {value}
    </span>
  </div>
);

export default PriceSuggestion;
